import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// 全屏登录页面组件
const AutoLogin = () => {
  const formRef = useRef(null);
  const location = useLocation();
  
  useEffect(() => {
    // 从URL查询参数中获取值
    const searchParams = new URLSearchParams(location.search);
    
    // 如果使用哈希路由，可能需要解析哈希后的查询参数
    // 检查是否有查询参数，如果没有则尝试解析哈希后的查询部分
    let usertoken = searchParams.get('usertoken');
    let carid = searchParams.get('carid');
    let zd_host = searchParams.get('zd_host');
    let checkToken = searchParams.get('checkToken');
    
    // 如果在正常查询参数中找不到，尝试从哈希中解析
    if (location.hash) {
      // 从哈希中提取查询参数部分
      const hashParts = location.hash.split('?');
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        usertoken = usertoken || hashParams.get('usertoken');
        carid = carid || hashParams.get('carid');
        zd_host = zd_host || hashParams.get('zd_host');
        checkToken = checkToken || hashParams.get('checkToken');
      }
    }
    
    // 设置cookie的过期时间为90天
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 90);
    
    // 如果有host参数，将其存入cookie
    if (zd_host) {
      document.cookie = `zd_host=${zd_host}; expires=${expiryDate.toUTCString()}; path=/`;
    }
    
    // 如果有checkToken参数，将其存入cookie
    if (checkToken) {
      document.cookie = `checkToken=${checkToken}; expires=${expiryDate.toUTCString()}; path=/`;
    }
    
    // 设置表单字段值并提交
    if (formRef.current) {
      // 如果查询参数中有值，则使用它们
      if (usertoken) {
        formRef.current.elements.usertoken.value = usertoken;
      }
      
      if (carid) {
        formRef.current.elements.carid.value = carid;
      }
      
      // 提交表单
      formRef.current.submit();
    }
    
    // 添加样式来覆盖整个页面
    const style = document.createElement('style');
    style.textContent = `
      /* 重置整个页面样式 */
      html, body, #root, #app, div[data-reactroot] {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
      }
      
      /* 隐藏可能存在的header和footer */
      header, footer, nav {
        display: none !important;
      }
      
      /* 确保我们的容器占满整个视口 */
      .fullscreen-login {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 9999;
        background-color: var(--bg-light);
      }
    `;
    document.head.appendChild(style);
    
    // 添加Font Awesome
    if (!document.querySelector('link[href*="font-awesome"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
      document.head.appendChild(link);
    }
    
    // 当组件卸载时清理
    return () => {
      document.head.removeChild(style);
    };
  }, [location]);

  return (
    <div className="fullscreen-login">
      <style>{`
        :root {
          /* 现代化紫色主题 */
          --primary-color: #6366f1;
          --primary-dark: #4f46e5;
          --primary-light: #a5b4fc;
          --accent-color: #8b5cf6;
          --text-color: #1e293b;
          --text-light: #64748b;
          --bg-color: #ffffff;
          --bg-light: #f8fafc;
          --card-bg: #ffffff;
          --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
        
        .fullscreen-login {
          color: var(--text-color);
          background-color: var(--bg-light);
          min-height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          line-height: 1.5;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background-image: 
              radial-gradient(circle at 10% 20%, rgba(99, 102, 241, 0.05) 0%, transparent 20%),
              radial-gradient(circle at 90% 80%, rgba(139, 92, 246, 0.05) 0%, transparent 20%),
              radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.03) 0%, transparent 50%);
        }
        
        .login-container {
          width: 100%;
          max-width: 420px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .login-card {
          background: var(--card-bg);
          border-radius: 16px;
          box-shadow: var(--shadow-lg);
          padding: 40px 30px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        
        .login-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
        }
        
        .logo {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 36px;
        }
        
        .logo-icon {
          font-size: 28px;
          color: var(--primary-color);
          margin-right: 10px;
        }
        
        .logo-text {
          font-size: 24px;
          font-weight: 700;
          color: var(--primary-color);
          letter-spacing: -0.5px;
        }
        
        .loader-container {
          margin: 30px 0;
        }
        
        .loader {
          display: inline-block;
          position: relative;
          width: 64px;
          height: 64px;
        }
        
        .loader div {
          position: absolute;
          top: 27px;
          width: 11px;
          height: 11px;
          border-radius: 50%;
          background: var(--primary-color);
          animation-timing-function: cubic-bezier(0, 1, 1, 0);
        }
        
        .loader div:nth-child(1) {
          left: 6px;
          animation: loader1 0.6s infinite;
        }
        
        .loader div:nth-child(2) {
          left: 6px;
          animation: loader2 0.6s infinite;
        }
        
        .loader div:nth-child(3) {
          left: 26px;
          animation: loader2 0.6s infinite;
        }
        
        .loader div:nth-child(4) {
          left: 45px;
          animation: loader3 0.6s infinite;
        }
        
        @keyframes loader1 {
          0% { transform: scale(0); }
          100% { transform: scale(1); }
        }
        
        @keyframes loader2 {
          0% { transform: translate(0, 0); }
          100% { transform: translate(19px, 0); }
        }
        
        @keyframes loader3 {
          0% { transform: scale(1); }
          100% { transform: scale(0); }
        }
        
        .message {
          font-size: 20px;
          font-weight: 600;
          color: var(--text-color);
          margin-bottom: 12px;
          margin-top: 0;
        }
        
        .sub-message {
          font-size: 15px;
          color: var(--text-light);
          max-width: 300px;
          margin: 0 auto;
        }
        
        .login-footer {
          margin-top: 32px;
          padding-top: 16px;
          border-top: 1px solid rgba(0,0,0,0.05);
          font-size: 14px;
          color: var(--text-light);
        }
        
        @media (max-width: 480px) {
          .login-card {
            padding: 30px 20px;
          }
          
          .message {
            font-size: 18px;
          }
          
          .sub-message {
            font-size: 14px;
          }
        }
      `}</style>
      
      <div className="login-container">
        <div className="login-card">
          <div className="logo">
            <i className="fas fa-robot logo-icon"></i>
            <div className="logo-text">紫刀AI</div>
          </div>
          
          <div className="loader-container">
            <div className="loader">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
          
          <h1 className="message">正在登录</h1>
          <p className="sub-message">请稍候，系统正在为您自动登录中...</p>
          
          <div className="login-footer">
            安全连接至紫刀AI账户服务
          </div>
        </div>
      </div>
      
      {/* 隐藏的表单，保持原有功能 */}
      <form 
        ref={formRef}
        id="autoSubmitForm" 
        action="https://zd2ai.org/auth/login" 
        method="POST" 
        style={{ display: 'none' }}
      >
        <input 
          type="hidden" 
          name="usertoken" 
          value="" // 初始值为空，将通过JavaScript设置
        />
        <input 
          type="hidden" 
          name="carid" 
          value="" // 初始值为空，将通过JavaScript设置
        />
      </form>
    </div>
  );
};

export default AutoLogin;